import { useEffect, useRef, useState } from 'react';
import { fetchMentionCandidates } from './api/fileReviewApi';
import './mentionEmail.css';

export interface MentionCandidatePickerProps {
  mountId: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
  query: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (mentionToken: string) => void;
}

export function MentionCandidatePicker({
  mountId,
  path,
  query,
  open,
  onOpenChange,
  onSelect
}: MentionCandidatePickerProps) {
  const [items, setItems] = useState<
    { displayName: string; notifyEmail: string; mentionToken: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open || !mountId) return;
    const id = ++requestId.current;
    setLoading(true);
    const handle = window.setTimeout(() => {
      void fetchMentionCandidates(mountId, query, path)
        .then((res) => {
          if (requestId.current !== id) return;
          setItems(
            (res.items ?? []).map((row) => ({
              displayName: row.displayName,
              notifyEmail: row.notifyEmail,
              mentionToken: row.mentionToken
            }))
          );
          setReason(res.reason ?? null);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setItems([]);
          setReason('mentions_unavailable');
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [mountId, open, path, query]);

  if (!open) return null;

  const emptyMessage =
    reason === 'mentions_require_group'
      ? 'Mentions need at least one Entra group on this storage mount.'
      : reason === 'no_shared_groups'
        ? 'No mentionable peers in your groups for this location.'
        : reason === 'mentions_disabled'
          ? 'Mentions are disabled.'
          : loading
            ? 'Loading…'
            : 'No matches. Pick from the list only—typing an email is not allowed.';

  return (
    <ul className="file-review-mention-picker" role="listbox" aria-label="Mention suggestions">
      {items.length === 0 ? (
        <li className="file-review-mention-picker__empty" role="option" aria-disabled>
          {emptyMessage}
        </li>
      ) : (
        items.map((item) => (
          <li key={item.notifyEmail} role="option">
            <button
              type="button"
              className="file-review-mention-picker__option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(item.mentionToken);
                onOpenChange(false);
              }}
            >
              <span className="file-review-mention-picker__name">{item.displayName}</span>
              <span className="file-review-mention-picker__email">{item.notifyEmail}</span>
            </button>
          </li>
        ))
      )}
    </ul>
  );
}
